import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchAuditTrail,
  downloadAuditExport,
  type AuditTrailField,
} from "../api/client";
import { Search, Download, CheckCircle2, Pencil, AlertCircle, Zap, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { prettyFieldLabel, prettyStatus, prettyFileType, prettyValue } from "../utils/labels";
import { useToastContext } from "../contexts/ToastContext";
import { friendlyError } from "../utils/errors";

const STATUS_CONFIG: Record<string, { bg: string; text: string; icon: typeof CheckCircle2 }> = {
  verified: { bg: "bg-sage-100", text: "text-sage-600", icon: CheckCircle2 },
  corrected: { bg: "bg-honey-100", text: "text-honey-700", icon: Pencil },
  unverified: { bg: "bg-grove-200", text: "text-bark-500", icon: AlertCircle },
};

export default function AuditTrailPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const [fields, setFields] = useState<AuditTrailField[]>([]);
  const [totalFields, setTotalFields] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [exporting, setExporting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [collapsedDocs, setCollapsedDocs] = useState<Set<string>>(new Set());
  const toast = useToastContext();

  const load = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const data = await fetchAuditTrail(dealId, params);
      setFields(data.fields);
      setTotalFields(data.total_fields);
    } catch (e) {
      setFields([]);
      const msg = e instanceof Error ? e.message : String(e);
      setLoadError(friendlyError(msg));
    } finally {
      setLoading(false);
    }
  }, [dealId, search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleExport = useCallback(async () => {
    if (!dealId) return;
    setExporting(true);
    try {
      await downloadAuditExport(dealId);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`PDF export failed: ${friendlyError(msg)}`);
    }
    setExporting(false);
  }, [dealId, toast]);

  const stats = useMemo(() => ({
    verified: fields.filter((f) => f.status === "verified").length,
    corrected: fields.filter((f) => f.status === "corrected").length,
    unverified: fields.filter((f) => f.status === "unverified").length,
  }), [fields]);

  // Group by document
  const docGroups = useMemo(() => {
    const map = new Map<string, { doc: string; fileType: string; docId: string; fields: AuditTrailField[] }>();
    for (const f of fields) {
      const key = f.source_doc_id;
      if (!map.has(key)) {
        map.set(key, { doc: f.source_doc, fileType: f.file_type, docId: f.source_doc_id, fields: [] });
      }
      map.get(key)!.fields.push(f);
    }
    return Array.from(map.values());
  }, [fields]);

  const toggleDoc = (docId: string) => {
    setCollapsedDocs((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId); else next.add(docId);
      return next;
    });
  };

  const statusChips = [
    { value: "", label: "All" },
    { value: "verified", label: "Verified" },
    { value: "corrected", label: "Corrected" },
    { value: "unverified", label: "Needs Review" },
  ];

  return (
    <div className="max-w-[1280px] mx-auto px-4 sm:px-8 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-xl font-semibold text-bark-800">Audit Trail</h1>
          <p className="text-sm text-bark-400 mt-0.5">{totalFields} fields across {docGroups.length} document{docGroups.length !== 1 ? "s" : ""}</p>
        </div>
        <button onClick={handleExport} disabled={exporting}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-forest-600 text-white hover:bg-forest-700 disabled:opacity-50 transition-colors">
          <Download className="w-4 h-4" />
          {exporting ? "Generating..." : "Export PDF"}
        </button>
      </div>

      {/* Stats + filters */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <div className="flex gap-3 text-sm">
          {[
            { label: "Verified", count: stats.verified, color: "text-sage-600" },
            { label: "Corrected", count: stats.corrected, color: "text-honey-600" },
            { label: "Needs Review", count: stats.unverified, color: "text-bark-400" },
          ].map(({ label, count, color }) => (
            <span key={label} className="flex items-center gap-1">
              <span className={`font-semibold tabular-nums ${color}`}>{count}</span>
              <span className="text-bark-400">{label}</span>
            </span>
          ))}
        </div>

        <div className="flex-1" />

        {/* Status chips */}
        <div className="flex gap-1">
          {statusChips.map((chip) => (
            <button key={chip.value} onClick={() => setStatusFilter(chip.value)}
              className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                statusFilter === chip.value
                  ? "bg-forest-600 text-white"
                  : "bg-grove-100 text-bark-500 hover:bg-grove-200"
              }`}>
              {chip.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bark-400" />
          <input type="text" placeholder="Search..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-3 py-1.5 text-sm border border-grove-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-forest-500 bg-white w-48" />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-forest-600 border-t-transparent" />
        </div>
      ) : loadError ? (
        <div className="flex flex-col items-center py-12 text-center">
          <AlertCircle className="w-6 h-6 text-red-500 mb-2" />
          <p className="text-sm text-bark-600">Could not load the audit trail.</p>
          <p className="text-xs text-bark-400 mt-1">{loadError}</p>
          <button onClick={load}
            className="mt-4 px-3 py-1.5 text-xs font-medium rounded-lg bg-forest-600 text-white hover:bg-forest-700 transition-colors">
            Retry
          </button>
        </div>
      ) : docGroups.length === 0 ? (
        <div className="text-center py-12 text-bark-400 text-sm">No fields match your filters.</div>
      ) : (
        <div className="space-y-4">
          {docGroups.map((group) => {
            const isCollapsed = collapsedDocs.has(group.docId);
            const groupVerified = group.fields.filter((f) => f.status !== "unverified").length;
            return (
              <div key={group.docId} className="border border-grove-300 rounded-lg overflow-hidden">
                {/* Document header */}
                <button onClick={() => toggleDoc(group.docId)}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-grove-50 hover:bg-grove-100 transition-colors text-left">
                  {isCollapsed ? <ChevronRight className="w-4 h-4 text-bark-400" /> : <ChevronDown className="w-4 h-4 text-bark-400" />}
                  <span className="font-medium text-bark-700 text-sm">{group.doc}</span>
                  <span className="text-xs text-bark-400">{prettyFileType(group.fileType)}</span>
                  <span className="ml-auto text-xs tabular-nums text-bark-500">
                    {groupVerified}/{group.fields.length} verified
                  </span>
                </button>

                {/* Field table */}
                {!isCollapsed && (
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-t border-grove-200 bg-white">
                        <th className="text-left px-4 py-2 font-medium text-bark-500 text-xs">Field</th>
                        <th className="text-right px-4 py-2 font-medium text-bark-500 text-xs">Parser Output</th>
                        <th className="text-right px-4 py-2 font-medium text-bark-500 text-xs">Final Value</th>
                        <th className="text-center px-4 py-2 font-medium text-bark-500 text-xs">Status</th>
                        <th className="text-left px-4 py-2 font-medium text-bark-500 text-xs">Verified By</th>
                        <th className="text-left px-4 py-2 font-medium text-bark-500 text-xs">When</th>
                        <th className="px-2 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {group.fields.map((f, i) => {
                        const badge = STATUS_CONFIG[f.status] || STATUS_CONFIG.unverified;
                        const Icon = badge.icon;
                        const changed = f.status === "corrected" && prettyValue(f.parser_output) !== prettyValue(f.final_value);
                        return (
                          <tr key={`${f.field_path}-${i}`} className="border-t border-grove-100 hover:bg-grove-50 transition-colors">
                            <td className="px-4 py-2">
                              <div className="font-medium text-bark-700 text-xs">{prettyFieldLabel(f.field_path)}</div>
                              {f.rule_applied && (
                                <span className="inline-flex items-center gap-0.5 text-[10px] text-sage-600">
                                  <Zap className="w-2.5 h-2.5" />{f.rule_applied}
                                </span>
                              )}
                            </td>
                            <td className={`px-4 py-2 text-right tabular-nums text-xs ${changed ? "text-bark-400 line-through" : "text-bark-600"}`}>
                              {prettyValue(f.parser_output)}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums text-xs font-medium text-bark-700">{prettyValue(f.final_value)}</td>
                            <td className="px-4 py-2 text-center">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full ${badge.bg} ${badge.text}`}>
                                <Icon className="w-3 h-3" />{prettyStatus(f.status)}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-bark-500 text-xs">{f.verified_by || "--"}</td>
                            <td className="px-4 py-2 text-bark-400 text-xs">{f.verified_at ? f.verified_at.slice(0, 10) : "--"}</td>
                            <td className="px-2 py-2">
                              <button onClick={() => navigate(`/deals/${dealId}/verify?doc=${f.source_doc_id}&field=${encodeURIComponent(f.field_path)}`)}
                                className="p-1 rounded text-bark-300 hover:text-forest-600 transition-colors" title="View in Verify">
                                <ExternalLink className="w-3 h-3" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
